'use client'

import {
  ArrowRight,
  Book,
  Building,
  Enterprise as Building2,
  Calculator,
  Screen as Computer,
  CurrencyDollar as DollarSign,
  Scales as Gavel,
  Earth as Globe2,
  Idea as Lightbulb,
  Lifesaver as LifeBuoy,
  Location as MapPin,
  Menu,
  Blog as Newspaper,
  ChartPie as PieChart,
  Integration as Puzzle,
  Rocket,
  Search,
  Chat as Speech,
  Group as Users,
  Close as X,
  type CarbonIconType as LucideIcon,
} from '@carbon/icons-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import {
  GitHubIcon,
  GlassdoorIcon,
  LinkedInIcon,
  MediumIcon,
} from '@/components/social-icons'
import { ThemeLogo } from '@/components/theme-logo'
import { WorldClock } from '@/components/world-clock'
import { ThemeModeToggle } from '@/components/theme-mode-toggle'
import { siteConfig } from '@/config/site'
import { cn } from '@/lib/utils'

// ─── Types ───────────────────────────────────────────────────────────────────

type TextLink = {
  title: string
  href: string
  external?: boolean
}

type RowLink = TextLink & {
  description?: string
  icon?: LucideIcon
}

type Column =
  | {
      kind: 'rows'
      label?: string
      width: string
      items: RowLink[]
      /**
       * Desktop only. Two-up lays the rows out as a real grid so paired cells
       * share a height and their separating rules line up across the columns —
       * two independent stacks drift apart as soon as one description wraps to
       * a different number of lines.
       */
      cols?: 1 | 2
    }
  | {
      /**
       * Two separately-labelled stacks that still share one grid, so paired
       * rows keep a common height and their rules line up straight across.
       * As two sibling columns they drift apart the moment one description
       * wraps to a different number of lines.
       */
      kind: 'rowPairs'
      width: string
      groups: { label: string; items: RowLink[] }[]
    }
  | { kind: 'list'; label?: string; width: string; items: TextLink[] }
  | {
      kind: 'groups'
      label?: string
      width: string
      groups: RegionGroup[]
    }

type RegionGroup = {
  title: string
  items: TextLink[]
  /**
   * Desktop only: take the whole column and fan the entries out across three
   * sub-columns. Reserved for the one oversized group — left in a single
   * stack it would set the height of the entire dropdown.
   */
  wide?: boolean
}

type NavSection = {
  key: string
  label: string
  /** Present when the trigger itself is also a destination. */
  href?: string
  columns: Column[]
}

// ─── Nav content ─────────────────────────────────────────────────────────────
// Column widths are chosen so the widest panel stays under 1024px — the `lg`
// breakpoint where the desktop bar first appears — since the shell is anchored
// to the nav's left edge and must not run off-screen.

const navSections: NavSection[] = [
  {
    key: 'solutions',
    label: 'Solutions',
    href: '/solutions',
    columns: [
      {
        kind: 'rows',
        width: 'w-[21rem]',
        items: [
          {
            title: 'Explore Our Solutions',
            description:
              'Transform your business with intelligent solutions that streamline operations, enhance productivity, and drive sustainable growth.',
            href: '/solutions',
            icon: Puzzle,
          },
          {
            title: 'Cloud Infrastructure',
            description:
              'Scalable cloud infrastructure powering your mission-critical applications.',
            href: '/solutions/cloud-infrastructure',
            icon: Globe2,
          },
          {
            title: 'AI Infrastructure',
            description:
              'Intelligent automation and machine learning solutions for smarter decisions.',
            href: '/solutions/ai-infrastructure',
            icon: Lightbulb,
          },
        ],
      },
      {
        kind: 'list',
        label: 'IntelQI Product Suite',
        width: 'w-56',
        items: [
          { title: 'UDC', href: '/products/udc' },
          { title: 'HRMS', href: '/products/hrms' },
          { title: 'Payroll', href: '/products/payroll' },
          { title: 'Accounts', href: '/products/accounts' },
          { title: 'LMS', href: '/products/lms' },
          { title: 'Schools', href: '/schoolerp' },
        ],
      },
      {
        kind: 'list',
        label: 'Cloud Infrastructure',
        width: 'w-56',
        items: [
          {
            title: 'Cloud Orchestration',
            href: '/solutions/cloud-infrastructure',
          },
          {
            title: 'Serverless Computing',
            href: '/solutions/cloud-infrastructure',
          },
          {
            title: 'Distributed Systems',
            href: '/solutions/cloud-infrastructure',
          },
          { title: 'Edge Computing', href: '/solutions/cloud-infrastructure' },
        ],
      },
      {
        kind: 'list',
        label: 'AI Infrastructure',
        width: 'w-56',
        items: [
          { title: 'Neural Networks', href: '/solutions/ai-infrastructure' },
          {
            title: 'Predictive Analytics',
            href: '/solutions/ai-infrastructure',
          },
          { title: 'Natural Language', href: '/solutions/ai-infrastructure' },
          { title: 'Computer Vision', href: '/solutions/ai-infrastructure' },
        ],
      },
    ],
  },
  {
    key: 'products',
    label: 'Products',
    href: '/products',
    columns: [
      // Six products in one stack made this panel a tall narrow strip next to
      // the others; a two-up grid keeps it in proportion.
      {
        kind: 'rows',
        cols: 2,
        width: 'w-[42rem] xl:w-[46rem]',
        items: [
          {
            title: 'UDC',
            description:
              'Unified Digital Campus. Seamlessly integrate and unify campus data with powerful orchestration.',
            href: '/products/udc',
            icon: Building2,
          },
          {
            title: 'HRMS',
            description:
              'HR Management System. Streamline workforce operations with comprehensive employee lifecycle management.',
            href: '/products/hrms',
            icon: Users,
          },
          {
            title: 'Payroll',
            description:
              'Payroll Management. Ensure accurate compensation with intelligent payroll processing and compliance.',
            href: '/products/payroll',
            icon: DollarSign,
          },
          {
            title: 'Accounts',
            description:
              'Accounting System. Modernize financial operations with automated processes and real-time insights.',
            href: '/products/accounts',
            icon: Calculator,
          },
          {
            title: 'LMS',
            description:
              'Learning Management System. Empower continuous learning with AI-powered personalized training experiences.',
            href: '/products/lms',
            icon: Book,
          },
          {
            title: 'School Management System',
            description:
              'Manage academics, communication, assignments, attendance, and daily school operations through one unified system.',
            href: '/schoolerp',
            icon: Building,
          },
        ],
      },
      {
        kind: 'list',
        label: 'Explore',
        width: 'w-56',
        items: [
          { title: 'Explore our Product Suite', href: '/products' },
          { title: 'Product Brochure', href: '/product-brochure' },
          { title: 'Product Updates', href: '/product-updates' },
          { title: 'Request Demo', href: '/schedule-meeting' },
        ],
      },
    ],
  },
  {
    key: 'company',
    label: 'Company',
    columns: [
      {
        // One grid across both stacks — same total width as the two columns
        // it replaces, but the paired rows now share a height.
        kind: 'rowPairs',
        width: 'w-[34rem] xl:w-[39rem]',
        groups: [
          {
            label: 'About Us',
            items: [
              {
                title: 'Company',
                description:
                  'Learn about Euclideum and our mission to transform businesses.',
                href: '/about-us',
                icon: Building2,
              },
              {
                title: 'Our Story',
                description:
                  'Discover the journey and vision behind our innovative solutions.',
                href: '/blogs/story',
                icon: Book,
              },
            ],
          },
          {
            label: 'Resources & Information',
            items: [
              {
                title: 'Customer Stories',
                description:
                  'See how organizations achieve success with our solutions.',
                href: '/customer-stories',
                icon: Lightbulb,
              },
              {
                title: 'Compliance',
                description:
                  'Learn about our security standards and compliance certifications.',
                href: '/compliance',
                icon: Gavel,
              },
            ],
          },
        ],
      },
      {
        kind: 'groups',
        label: 'Our Presence Across India',
        width: 'w-[28rem] xl:w-[29rem]',
        groups: [
          {
            title: 'Maharashtra',
            wide: true,
            items: [
              { title: 'Mumbai', href: '/service-locations/mumbai' },
              { title: 'Navi Mumbai', href: '/service-locations/navi-mumbai' },
              { title: 'Pune', href: '/service-locations/pune' },
              { title: 'Nagpur', href: '/service-locations/nagpur' },
              { title: 'Nashik', href: '/service-locations/nashik' },
              { title: 'Kolhapur', href: '/service-locations/kolhapur' },
              { title: 'Sangli', href: '/service-locations/sangli' },
              { title: 'Dhule', href: '/service-locations/dhule' },
            ],
          },
          {
            title: 'North India',
            items: [
              { title: 'Delhi', href: '/service-locations/delhi' },
              { title: 'Dehradun', href: '/service-locations/dehradun' },
              {
                title: 'Uttar Pradesh',
                href: '/service-locations/uttar-pradesh',
              },
            ],
          },
          {
            title: 'South India',
            items: [
              { title: 'Bangalore', href: '/service-locations/bangalore' },
              { title: 'Hyderabad', href: '/service-locations/hyderabad' },
              { title: 'Karnataka', href: '/service-locations/karnataka' },
            ],
          },
          {
            title: 'Central & East India',
            items: [
              { title: 'Indore', href: '/service-locations/indore' },
              { title: 'Gujarat', href: '/service-locations/gujarat' },
              { title: 'Bengal', href: '/service-locations/bengal' },
              { title: 'Jharkhand', href: '/service-locations/jharkhand' },
            ],
          },
        ],
      },
    ],
  },
  {
    key: 'resources',
    label: 'Resources',
    columns: [
      {
        kind: 'rows',
        label: 'Resources & Learning',
        width: 'w-[22rem]',
        items: [
          {
            title: 'Blogs',
            description: 'Latest updates and best practices.',
            href: '/blogs',
            icon: Newspaper,
          },
          {
            title: 'Product Brochure',
            description: 'Download our latest product brochure.',
            href: '/product-brochure',
            icon: Calculator,
          },
          {
            title: 'Product Updates',
            description: 'Latest features and improvements.',
            href: '/product-updates',
            icon: Rocket,
          },
        ],
      },
      {
        kind: 'list',
        label: 'Socials',
        width: 'w-56',
        items: [
          {
            title: 'LinkedIn',
            href: 'https://www.linkedin.com/company/euclideum/',
            external: true,
          },
          {
            title: 'Glassdoor',
            href: 'https://www.glassdoor.co.in/Overview/Working-at-EUCLIDEUM-SOLUTIONS-EI_IE10895753.11,30.htm',
            external: true,
          },
          {
            title: 'GitHub',
            href: 'https://github.com/IntelQI',
            external: true,
          },
          {
            title: 'Medium',
            href: 'https://intelqi.medium.com/#',
            external: true,
          },
        ],
      },
    ],
  },
]

/**
 * Official brand marks for the socials list — kept out of the data so `list`
 * columns stay a single uniform shape, and looked up only where a glyph
 * actually helps. Typed loosely enough to hold both these inline SVGs and the
 * Carbon glyphs used by the solution lists below.
 */
type NavIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>

const socialIcons: Record<string, NavIcon> = {
  LinkedIn: LinkedInIcon,
  Glassdoor: GlassdoorIcon,
  GitHub: GitHubIcon,
  Medium: MediumIcon,
}

// Icons used by the mobile drawer's flat solution lists.
const listIcons: Record<string, NavIcon> = {
  'Cloud Orchestration': Globe2,
  'Serverless Computing': Rocket,
  'Distributed Systems': Puzzle,
  'Edge Computing': Computer,
  'Neural Networks': Lightbulb,
  'Predictive Analytics': PieChart,
  'Natural Language': Speech,
  'Computer Vision': Search,
}

// ─── Shared primitives ───────────────────────────────────────────────────────

/** Lets any link inside a panel dismiss the menu it was clicked in. */
const DismissContext = createContext<() => void>(() => {})

function PanelLink({
  href,
  external,
  className,
  children,
}: {
  href: string
  external?: boolean
  className?: string
  children: React.ReactNode
}) {
  const dismiss = useContext(DismissContext)

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={className}
        onClick={dismiss}
      >
        {children}
      </a>
    )
  }

  return (
    <Link href={href} className={className} onClick={dismiss}>
      {children}
    </Link>
  )
}

/** Mono uppercase eyebrow that heads a column. */
function ColumnLabel({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <p
      className={cn(
        'px-5 pb-3 pt-5 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground',
        className
      )}
    >
      {children}
    </p>
  )
}

/**
 * Renders N labelled row stacks into a single two-up grid. Emitting the cells
 * row-major (both labels, then each pair of rows) lets grid auto-placement do
 * the alignment: every grid row is as tall as its tallest cell, so the rules
 * between rows meet across the divider.
 */
function RowPairGrid({
  groups,
}: {
  groups: { label: string; items: RowLink[] }[]
}) {
  const rowCount = Math.max(...groups.map((group) => group.items.length))
  const edge = (index: number) => (index > 0 ? 'border-l border-border' : '')

  return (
    <div
      className="grid flex-1 grid-cols-2"
      // Labels size to content; every content row takes an equal share of
      // whatever height the panel's tallest column forces on this one, so the
      // grid ends flush with the panel floor instead of trailing an empty
      // ruled band under the last row.
      style={{
        gridTemplateRows: `auto repeat(${rowCount}, minmax(0, 1fr))`,
      }}
    >
      {groups.map((group, index) => (
        <ColumnLabel key={group.label} className={edge(index)}>
          {group.label}
        </ColumnLabel>
      ))}

      {Array.from({ length: rowCount }).flatMap((_, row) =>
        groups.map((group, index) => {
          const item = group.items[row]

          // A group with fewer entries still needs its cell, or the grid
          // would pull the next row's items into the gap.
          return item ? (
            <RowLinkItem
              key={item.title + item.href}
              item={item}
              className={edge(index)}
            />
          ) : (
            <div
              key={`${group.label}-blank-${row}`}
              className={cn('border-t border-border', edge(index))}
            />
          )
        })
      )}
    </div>
  )
}

/**
 * Rows are separated by a top rule. The very first row drops it only when no
 * eyebrow sits above the stack — when there is one, that rule is what divides
 * the label from the list.
 */
function RowStack({
  label,
  items,
  cols = 1,
  fill = false,
}: {
  label?: string
  items: RowLink[]
  cols?: 1 | 2
  /**
   * Desktop only. Take the column's leftover height and share it out between
   * the rows, so a short stack standing beside a tall column fills its cell
   * instead of trailing dead space under the last divider. Restricted to the
   * single-column shape: the two-up grid is already the tallest thing in its
   * panel, and equalising its rows would grow the dropdown, not fill it.
   */
  fill?: boolean
}) {
  const stretch = fill && cols === 1

  return (
    <div
      className={cn(
        cols === 2 && 'grid grid-cols-2',
        stretch && 'flex flex-1 flex-col'
      )}
    >
      {items.map((item, index) => (
        <RowLinkItem
          key={item.title + item.href}
          item={item}
          className={cn(
            // Whole top row, not just the first cell.
            !label && index < cols && 'border-t-0',
            cols === 2 && index % 2 === 1 && 'border-l border-border',
            // `grow` and not `flex-1`: rows keep their content height and only
            // split what's spare, so a long description still gets more room
            // than a short one.
            stretch && 'grow'
          )}
        />
      ))}
    </div>
  )
}

/**
 * The signature row: on hover everything slides right by 32px while an arrow
 * rides in from the left to fill the vacated slot and the trailing arrow exits
 * right. `overflow-hidden` is what makes both arrows appear to travel through
 * the row's edges rather than pop.
 */
function RowLinkItem({
  item,
  className,
}: {
  item: RowLink
  className?: string
}) {
  const Icon = item.icon

  return (
    <PanelLink
      href={item.href}
      external={item.external}
      className={cn(
        'group/row relative flex items-center gap-3.5 overflow-hidden border-t border-border px-5 py-4 transition-colors hover:bg-accent',
        className
      )}
    >
      <ArrowRight
        aria-hidden
        className="absolute size-[22px] shrink-0 -translate-x-12 text-primary transition-transform delay-[50ms] duration-300 group-hover/row:translate-x-0 motion-reduce:transition-none"
      />

      {Icon ? (
        <Icon
          aria-hidden
          className="size-[22px] shrink-0 text-primary transition-transform duration-300 group-hover/row:translate-x-9 motion-reduce:transition-none"
        />
      ) : null}

      <span className="flex w-full min-w-0 flex-col transition-transform duration-300 group-hover/row:translate-x-9 motion-reduce:transition-none">
        <span className="text-[15px] font-semibold leading-snug text-foreground">
          {item.title}
        </span>
        {item.description ? (
          <span className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
            {item.description}
          </span>
        ) : null}
      </span>

      <ArrowRight
        aria-hidden
        className="relative size-[18px] shrink-0 text-muted-foreground transition-transform duration-300 group-hover/row:translate-x-12 motion-reduce:transition-none"
      />
    </PanelLink>
  )
}

/** Compact text link: a hairline wipes in under the label on hover. */
function ListLinkItem({
  item,
  className,
}: {
  item: TextLink
  className?: string
}) {
  // Socials carry their brand mark; every other list stays plain text.
  const Brand = socialIcons[item.title]

  return (
    <PanelLink
      href={item.href}
      external={item.external}
      className={cn('group/list block px-5 py-1.5', className)}
    >
      <span className="relative flex w-fit items-center gap-1 pb-0.5 text-sm leading-5 text-foreground">
        {Brand ? (
          <Brand
            aria-hidden
            className="mr-1.5 size-4 shrink-0 text-foreground"
          />
        ) : null}
        {item.title}
        <ArrowRight
          aria-hidden
          className="size-3.5 shrink-0 -translate-x-2 opacity-0 transition-all duration-200 group-hover/list:translate-x-0 group-hover/list:opacity-100 motion-reduce:transition-none"
        />
        <span
          aria-hidden
          className="absolute bottom-0 left-0 h-px w-0 bg-foreground transition-[width] duration-300 group-hover/list:w-full motion-reduce:transition-none"
        />
      </span>
    </PanelLink>
  )
}

/** One panel column, rendered identically wherever it appears. */
function PanelColumn({ column }: { column: Column }) {
  return (
    // Column stack, so a `rows` child can claim the height the panel's tallest
    // column forces on this one (the panel row is items-stretch).
    <div className={cn('flex shrink-0 flex-col', column.width)}>
      {/* rowPairs carries a label per sub-column instead, rendered inside its
          own grid so the eyebrows sit on either side of the divider. */}
      {'label' in column && column.label ? (
        <ColumnLabel>{column.label}</ColumnLabel>
      ) : null}

      {column.kind === 'rows' ? (
        <RowStack
          label={column.label}
          items={column.items}
          cols={column.cols}
          fill
        />
      ) : null}

      {column.kind === 'rowPairs' ? (
        <RowPairGrid groups={column.groups} />
      ) : null}

      {column.kind === 'list' ? (
        <div className="pb-4">
          {column.items.map((item) => (
            <ListLinkItem key={item.title + item.href} item={item} />
          ))}
        </div>
      ) : null}

      {/* Regions share a three-up grid: the one flagged `wide` spans it and
          fans its own entries out three-across, so a long list can't stretch
          the whole dropdown taller than the columns beside it. */}
      {column.kind === 'groups' ? (
        <div className="grid grid-cols-3 gap-x-3 gap-y-4 px-5 pb-5">
          {column.groups.map((group) => (
            <div key={group.title} className={cn(group.wide && 'col-span-3')}>
              {/* Side-by-side titles reserve two lines so a name that wraps
                  ("Central & East India") doesn't shunt its list out of step
                  with its neighbours. */}
              <p
                className={cn(
                  'pb-1 text-[13px] font-semibold leading-tight text-foreground',
                  !group.wide && 'min-h-9'
                )}
              >
                {group.title}
              </p>
              <div className={cn('grid', group.wide && 'grid-cols-3 gap-x-3')}>
                {group.items.map((item) => (
                  <ListLinkItem key={item.href} item={item} className="px-0" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

// ─── Desktop dropdown shell ──────────────────────────────────────────────────

/** Cell styling shared by every full-height item in the bar. */
const cellClass =
  'flex h-[var(--header-h)] items-center px-5 text-[15px] transition-colors hover:bg-accent'

function DesktopNav() {
  const [active, setActive] = useState<number | null>(null)
  // Held separately from `active` so the shell keeps its last measured width
  // while collapsing, instead of snapping narrow as it fades out. `offset`
  // is the (never positive) nudge that keeps a wide panel on-screen.
  const [dims, setDims] = useState({ width: 0, height: 0, offset: 0 })
  const panelRefs = useRef<Array<HTMLDivElement | null>>([])
  const wrapperRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const open = useCallback(
    (index: number) => {
      cancelClose()
      setActive(index)
    },
    [cancelClose]
  )

  // A short grace period keeps the panel alive across the 1px divider between
  // two triggers and while the pointer crosses into the panel itself.
  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => setActive(null), 120)
  }, [cancelClose])

  const close = useCallback(() => {
    cancelClose()
    setActive(null)
  }, [cancelClose])

  useEffect(() => cancelClose, [cancelClose])

  // Measure before paint so the width/height transition starts from the real
  // geometry rather than flashing a frame at the previous panel's size.
  const measure = useCallback(() => {
    if (active === null) return
    const el = panelRefs.current[active]
    const wrapper = wrapperRef.current
    if (!el || !wrapper) return

    const width = el.offsetWidth
    const height = el.offsetHeight

    // The shell hangs off the nav's left edge, so a wide panel on a narrow
    // desktop would push past the right edge and give the page a horizontal
    // scrollbar. Slide it back left by just enough — and never so far that it
    // leaves the viewport on the other side.
    const left = wrapper.getBoundingClientRect().left
    const available = document.documentElement.clientWidth - 8
    const offset = Math.max(-left, Math.min(0, available - (left + width)))

    setDims({ width, height, offset })
  }, [active])

  useLayoutEffect(measure, [measure])

  useEffect(() => {
    if (active === null) return
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [active, measure])

  useEffect(() => {
    if (active === null) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [active, close])

  const isOpen = active !== null

  return (
    <DismissContext.Provider value={close}>
      <div
        ref={wrapperRef}
        className="relative hidden w-full flex-col justify-center self-stretch lg:flex"
        onMouseLeave={scheduleClose}
        onMouseEnter={cancelClose}
      >
        {/* Dims and blurs the page under the open panel. Kept
            pointer-events-none on purpose: it lives inside the hover wrapper,
            so if it could be hit-tested the wrapper would never see the
            mouseleave that closes the menu. */}
        <div
          aria-hidden
          className={cn(
            'pointer-events-none fixed inset-0 top-[var(--header-h)] z-40 bg-background/60 backdrop-blur-sm',
            'transition-opacity duration-300 ease-out motion-reduce:transition-none',
            isOpen ? 'opacity-100' : 'opacity-0'
          )}
        />

        <nav className="relative z-50 flex h-full items-stretch divide-x divide-border">
          {navSections.map((section, index) => {
            const shared = {
              className: cn(
                cellClass,
                'border-border font-medium last:border-r',
                active === index && 'bg-accent'
              ),
              onMouseEnter: () => open(index),
              onFocus: () => open(index),
              'aria-expanded': active === index,
              'aria-haspopup': true as const,
            }

            return section.href ? (
              <Link key={section.key} href={section.href} {...shared}>
                {section.label}
              </Link>
            ) : (
              <button
                key={section.key}
                type="button"
                onClick={() => (active === index ? close() : open(index))}
                {...shared}
              >
                {section.label}
              </button>
            )
          })}
        </nav>

        {/* One shell for every panel. Width and height are driven from the
            measured panel so switching sections morphs the box. */}
        <div
          className={cn(
            // Frosted to match the bar it hangs from; the scrim behind it
            // already mutes the page, so 85% keeps the copy fully legible.
            'absolute top-full z-50 overflow-hidden rounded-b-xl border border-border bg-popover/85 shadow-2xl backdrop-blur-xl',
            'transition-[height,width,opacity] duration-300 ease-out motion-reduce:transition-none',
            isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
          style={{
            // -1px tucks the shell's border under the divider it hangs from.
            left: dims.offset - 1,
            height: isOpen ? dims.height : 0,
            width: dims.width || undefined,
          }}
        >
          {navSections.map((section, index) => (
            <div
              key={section.key}
              ref={(el) => {
                panelRefs.current[index] = el
              }}
              // `inert` (not display:none) keeps hidden panels out of the tab
              // order while leaving them laid out, which is what makes them
              // measurable for the width/height morph. They are stacked on top
              // of each other, so the later siblings would otherwise swallow
              // hover on whichever panel is actually showing — hence the
              // explicit pointer-events-none rather than relying on inert's
              // hit-testing behaviour.
              inert={active !== index}
              className={cn(
                'absolute left-0 top-0 flex w-max items-stretch divide-x divide-border',
                'transition-opacity duration-200 motion-reduce:transition-none',
                active === index
                  ? 'opacity-100'
                  : 'pointer-events-none opacity-0'
              )}
            >
              {section.columns.map((column, columnIndex) => (
                <PanelColumn key={columnIndex} column={column} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </DismissContext.Provider>
  )
}

// ─── Mobile drawer ───────────────────────────────────────────────────────────

function MobileColumn({ column }: { column: Column }) {
  return (
    <div className="border-t border-border first:border-t-0">
      {/* rowPairs carries a label per sub-column instead, rendered inside its
          own grid so the eyebrows sit on either side of the divider. */}
      {'label' in column && column.label ? (
        <ColumnLabel>{column.label}</ColumnLabel>
      ) : null}

      {column.kind === 'rows' ? (
        <RowStack label={column.label} items={column.items} />
      ) : null}

      {/* Narrow screens get the stacks one after another — pairing them up
          only matters where they sit side by side. */}
      {column.kind === 'rowPairs'
        ? column.groups.map((group) => (
            <div key={group.label}>
              <ColumnLabel>{group.label}</ColumnLabel>
              <RowStack label={group.label} items={group.items} />
            </div>
          ))
        : null}

      {column.kind === 'list' ? (
        <div className="grid grid-cols-2 gap-x-2 pb-4 pt-1">
          {column.items.map((item) => {
            const Icon = socialIcons[item.title] ?? listIcons[item.title]
            return (
              <PanelLink
                key={item.title + item.href}
                href={item.href}
                external={item.external}
                className="flex items-start gap-2 px-5 py-2 text-sm leading-snug text-foreground"
              >
                {Icon ? (
                  <Icon
                    aria-hidden
                    className="mt-0.5 size-4 shrink-0 text-primary"
                  />
                ) : null}
                <span>{item.title}</span>
              </PanelLink>
            )
          })}
        </div>
      ) : null}

      {column.kind === 'groups' ? (
        <div className="space-y-3 pb-4 pt-1">
          {column.groups.map((group) => (
            <div key={group.title}>
              <p className="px-5 pb-1 text-[13px] font-semibold text-foreground">
                {group.title}
              </p>
              <div className="grid grid-cols-2">
                {group.items.map((item) => (
                  <PanelLink
                    key={item.href}
                    href={item.href}
                    className="flex items-center gap-1.5 px-5 py-1.5 text-sm text-muted-foreground"
                  >
                    <MapPin
                      aria-hidden
                      className="size-3.5 shrink-0 opacity-60"
                    />
                    <span className="truncate">{item.title}</span>
                  </PanelLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

// ─── Navbar ──────────────────────────────────────────────────────────────────

export function Navbar() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [submenu, setSubmenu] = useState<number | null>(null)

  const closeDrawer = useCallback(() => {
    setOpen(false)
    setSubmenu(null)
  }, [])

  // Any navigation dismisses whatever is open.
  useEffect(() => {
    closeDrawer()
  }, [pathname, closeDrawer])

  // Lock the page behind the drawer.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (submenu !== null) setSubmenu(null)
      else closeDrawer()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, submenu, closeDrawer])

  const inSubmenu = open && submenu !== null

  return (
    /* Full-bleed. The frosted backdrop on <header> already spans the viewport,
       so capping this row at 1400px left the bar floating in the middle of its
       own background — visible as empty gutters either side that grew with the
       viewport (253px/268px at 1920, 573px/588px at 2560). The side borders
       went with it: they existed to frame the centred row, and at full width
       they would just be hairlines on the viewport edge. */
    <div className="flex h-[var(--header-h)] w-full items-stretch divide-x divide-border">
      {/* Logo cell — morphs into a back arrow while a mobile submenu is open. */}
      <Link
        href="/"
        aria-label={inSubmenu ? 'Back' : 'Go to homepage'}
        onClick={(event) => {
          if (!inSubmenu) return
          event.preventDefault()
          setSubmenu(null)
        }}
        className="grid h-[var(--header-h)] w-[var(--header-h)] shrink-0 place-content-center overflow-hidden transition-colors hover:bg-accent lg:w-auto lg:px-4"
      >
        <span
          className={cn(
            'col-start-1 row-start-1 flex items-center gap-2 transition-transform duration-300 motion-reduce:transition-none',
            inSubmenu
              ? 'translate-x-[calc(var(--header-h)*-1)]'
              : 'translate-x-0'
          )}
        >
          <ThemeLogo width={22} />
          {/* Mark only until the bar goes wide. Keeping this cell narrow below
              `xl` is what leaves room for the widest dropdown panel, which is
              anchored to the nav's left edge. */}
          <span className="hidden font-heading text-lg font-normal tracking-tighter xl:inline">
            {siteConfig.name}
          </span>
        </span>

        <span
          aria-hidden
          className={cn(
            // place-self, not justify-self: the grid row is as tall as the
            // logo mark, so a stretched span would pin this icon to its top.
            'col-start-1 row-start-1 place-self-center transition-transform duration-300 motion-reduce:transition-none lg:hidden',
            inSubmenu ? 'translate-x-0' : 'translate-x-[var(--header-h)]'
          )}
        >
          <ArrowRight className="size-6 rotate-180" />
        </span>
      </Link>

      {/* ── Mobile bar ─────────────────────────────────────────────────── */}
      <div className="flex w-full items-stretch divide-x divide-border lg:hidden">
        {/* Centre cell: the CTA rolls up and "MENU" rolls down in its place. */}
        <div className="relative h-[var(--header-h)] w-full overflow-hidden">
          <Link
            href="/schedule-meeting"
            className={cn(
              'absolute inset-0 flex items-center justify-center gap-2 text-base font-medium transition-transform duration-300 motion-reduce:transition-none',
              open
                ? 'pointer-events-none translate-y-[var(--header-h)]'
                : 'translate-y-0'
            )}
            tabIndex={open ? -1 : undefined}
          >
            Request Demo
            <ArrowRight className="size-[18px]" />
          </Link>

          <p
            aria-hidden
            className={cn(
              'absolute inset-0 flex items-center justify-center font-mono text-xs uppercase tracking-[0.14em] transition-transform duration-300 motion-reduce:transition-none',
              open ? 'translate-y-0' : 'translate-y-[calc(var(--header-h)*-1)]'
            )}
          >
            Menu
          </p>
        </div>

        <ThemeModeToggle
          unstyled
          className="h-[var(--header-h)] w-[var(--header-h)] shrink-0 hover:bg-accent"
        />

        <button
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => (open ? closeDrawer() : setOpen(true))}
          className="flex h-[var(--header-h)] w-[var(--header-h)] shrink-0 items-center justify-center transition-colors hover:bg-accent"
        >
          {open ? <X className="size-6" /> : <Menu className="size-6" />}
        </button>
      </div>

      {/* ── Desktop nav ────────────────────────────────────────────────── */}
      <DesktopNav />

      {/* Live clock — UTC, IST rolls in on hover. border-l-0 opts out of the
          bar root's divide-x so it reads as bare text, not a bordered cell. */}
      <WorldClock className="hidden shrink-0 !border-l-0 lg:flex" />

      {/* ── Desktop actions ────────────────────────────────────────────── */}
      <div className="hidden shrink-0 items-stretch divide-x divide-border lg:flex">
        <Link href="/support" className={cn(cellClass, 'gap-2')}>
          <LifeBuoy className="size-[18px]" />
          Support
        </Link>

        <ThemeModeToggle
          unstyled
          className="h-[var(--header-h)] w-[var(--header-h)] shrink-0 hover:bg-accent"
        />

        {/* Solid CTA with the same arrow hand-off used inside the panels. */}
        <div className="flex items-stretch">
          <Link
            href="/schedule-meeting"
            className="group/cta relative flex h-[var(--header-h)] items-center gap-2 overflow-hidden bg-primary px-6 text-[15px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <ArrowRight
              aria-hidden
              className="absolute left-6 size-[18px] -translate-x-12 transition-transform delay-[50ms] duration-300 group-hover/cta:translate-x-0 motion-reduce:transition-none"
            />
            <span className="transition-transform duration-300 group-hover/cta:translate-x-7 motion-reduce:transition-none">
              Request Demo
            </span>
            <ArrowRight
              aria-hidden
              className="relative size-[18px] transition-transform duration-300 group-hover/cta:translate-x-12 motion-reduce:transition-none"
            />
          </Link>
        </div>
      </div>

      {/* ── Mobile drawer ──────────────────────────────────────────────── */}
      <DismissContext.Provider value={closeDrawer}>
        <div
          inert={!open}
          className={cn(
            // border-l-0 opts out of the parent row's divide-x — this panel is
            // fixed, not one of the bar's cells.
            'fixed left-0 top-[var(--header-h)] z-40 h-[calc(100dvh-var(--header-h))] w-full overflow-hidden border-l-0 border-t border-border bg-background/95 backdrop-blur-xl dark:bg-[#09090b]/95 lg:hidden',
            'motion-reduce:transition-none',
            // Closed: visibility:hidden as well as translated away, so the
            // drawer is never painted — an off-screen transform alone still
            // rasterises, and Lighthouse was picking the drawer's content as
            // the page's LCP element. The visibility flip is instant on open
            // and delayed past the slide on close, so both animations play.
            open
              ? 'visible translate-x-0 [transition:transform_300ms_cubic-bezier(0,0,0.2,1)]'
              : 'invisible pointer-events-none translate-x-full [transition:transform_300ms_cubic-bezier(0,0,0.2,1),visibility_0s_300ms]'
          )}
        >
          {/* Level 1 — section list */}
          <div
            inert={submenu !== null}
            className={cn(
              'absolute inset-0 flex flex-col transition-transform duration-300 ease-out motion-reduce:transition-none',
              submenu !== null
                ? 'pointer-events-none -translate-x-full'
                : 'translate-x-0'
            )}
          >
            <div className="flex-1 divide-y divide-border overflow-y-auto">
              {navSections.map((section, index) => (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setSubmenu(index)}
                  className="flex w-full items-center justify-between p-5 text-left transition-colors hover:bg-accent"
                >
                  <span className="text-xl font-medium">{section.label}</span>
                  <ArrowRight className="size-[18px] shrink-0 text-muted-foreground" />
                </button>
              ))}

              <Link
                href="/support"
                className="flex w-full items-center justify-between p-5 transition-colors hover:bg-accent"
              >
                <span className="flex items-center gap-2.5 text-xl font-medium">
                  <LifeBuoy className="size-[18px] text-muted-foreground" />
                  Support
                </span>
                <ArrowRight className="size-[18px] shrink-0 text-muted-foreground" />
              </Link>
            </div>

            <div className="shrink-0">
              <Link
                href="/schedule-meeting"
                className="group/cta relative flex h-16 items-center justify-center gap-2 overflow-hidden border-t border-border bg-primary text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Book a Product Demo
                <ArrowRight className="size-4 transition-transform duration-300 group-hover/cta:translate-x-1 motion-reduce:transition-none" />
              </Link>
            </div>
          </div>

          {/* Level 2 — one panel per section, parked off-screen right */}
          {navSections.map((section, index) => (
            <div
              key={section.key}
              inert={submenu !== index}
              className={cn(
                'absolute inset-0 overflow-y-auto transition-transform duration-300 ease-out motion-reduce:transition-none',
                submenu === index
                  ? 'translate-x-0'
                  : 'pointer-events-none translate-x-full'
              )}
            >
              <h2 className="border-b border-border p-5 text-xl font-semibold">
                {section.label}
              </h2>
              {section.columns.map((column, columnIndex) => (
                <MobileColumn key={columnIndex} column={column} />
              ))}
            </div>
          ))}
        </div>
      </DismissContext.Provider>
    </div>
  )
}
